import * as React from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, IconButton, Chip, Typography, Box } from '@mui/material'
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import TitleScreenCrud from '../../../shared/TitleScreenCrud'
import MDBox from '../../../shared/MDBox'
import MDTypography from '../../../shared/MDTypography'
import agendaService from '../../agendaService'
import professorService from '../../../professores/professorService'
import { IAgendamento } from '../../AgendaType'
import { IProfessor } from '../../../professores/ProfessorType'
import clienteService from '../../../../services/ClienteService'
import {
  GradeWrapper, GradeHeaderRow, GradeHeaderCell, GradeBody,
  GradeTimeColumn, GradeTimeSlot, GradeDaysGrid, GradeDayColumn,
  GradeHourLine, BlocoHorario, CargaHorariaCard, CargaBar, ModalClienteItem
} from './AgendaForm.styled'

const DIAS_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex']
const DIAS_SEMANA_FULL = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta']
const MINUTOS = ['00', '15', '30', '45']
const HOUR_HEIGHT = 60
const START_HOUR = 8
const DEFAULT_END_HOUR = 22

const COR_PROFESSORES = [
  '#e3f2fd', '#fce4ec', '#e8f5e9', '#fff3e0', '#f3e5f5',
  '#e0f7fa', '#fff9c4', '#f1f8e9', '#fbe9e7', '#ede7f6'
]

const COR_BORDA_PROFESSORES = [
  '#1976d2', '#c62828', '#388e3c', '#ef6c00', '#7b1fa2',
  '#00838f', '#f9a825', '#558b2f', '#d84315', '#512da8'
]

const COR_MAP: Record<string, { bg: string; border: string }> = {}

function getCorProfessor(professorId: string): { bg: string; border: string } {
  if (!COR_MAP[professorId]) {
    const idx = Object.keys(COR_MAP).length % COR_PROFESSORES.length
    COR_MAP[professorId] = { bg: COR_PROFESSORES[idx], border: COR_BORDA_PROFESSORES[idx] }
  }
  return COR_MAP[professorId]
}

function formatarDataLocal(data: Date): string {
  const ano = data.getFullYear()
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${ano}-${mes}-${dia}`
}

function horasParaMinutos(h: string): number {
  const [hh, mm] = h.split(':').map(Number)
  return hh * 60 + mm
}

function calcularPosicaoAgendamento(a: IAgendamento): { top: number; height: number } {
  const inicio = a.horaInicio?.substring(11, 16) || '08:00'
  const fim = a.horaFim?.substring(11, 16) || '09:00'
  const minInicio = horasParaMinutos(inicio)
  const minFim = horasParaMinutos(fim)
  return {
    top: ((minInicio - START_HOUR * 60) / 60) * HOUR_HEIGHT,
    height: Math.max(((minFim - minInicio) / 60) * HOUR_HEIGHT, 24),
  }
}

export default function AgendaForm() {
  const [agendamentos, setAgendamentos] = React.useState<IAgendamento[]>([])
  const [professores, setProfessores] = React.useState<IProfessor[]>([])
  const [clientes, setClientes] = React.useState<unknown[]>([])
  const [semanaOffset, setSemanaOffset] = React.useState(0)

  const HORAS = React.useMemo(() => {
    let maxHour = DEFAULT_END_HOUR
    agendamentos.forEach(a => {
      const fim = a.horaFim?.substring(11, 16)
      if (fim) {
        const [hh] = fim.split(':').map(Number)
        if (hh + 1 > maxHour) maxHour = hh + 1
      }
    })
    return Array.from({ length: maxHour - START_HOUR }, (_, i) => START_HOUR + i)
  }, [agendamentos])

  const HORARIOS_COMPLETOS = React.useMemo(() =>
    HORAS.flatMap(h =>
      MINUTOS.filter(m => !(h === 22 && m !== '00')).map(m => `${String(h).padStart(2, '0')}:${m}`)
    ).filter(h => h <= '22:00'),
    [HORAS]
  )

  const [modalFormOpen, setModalFormOpen] = React.useState(false)
  const [modalDetalheOpen, setModalDetalheOpen] = React.useState(false)
  const [clientesNoHorario, setClientesNoHorario] = React.useState<IAgendamento[]>([])
  const [modalHorarioInfo, setModalHorarioInfo] = React.useState('')
  const [modalSlotData, setModalSlotData] = React.useState<Date | null>(null)
  const [modalSlotHora, setModalSlotHora] = React.useState<number>(0)

  const [editandoId, setEditandoId] = React.useState<string | null>(null)
  const [formProfessorId, setFormProfessorId] = React.useState('')
  const [formClienteId, setFormClienteId] = React.useState('')
  const [formDiaSemana, setFormDiaSemana] = React.useState(0)
  const [formHoraInicio, setFormHoraInicio] = React.useState('08:00')
  const [formHoraFim, setFormHoraFim] = React.useState('09:00')

  const getDiasDaSemana = React.useMemo(() => {
    const hoje = new Date()
    const diaSemanaAtual = hoje.getDay()
    const diffSegunda = diaSemanaAtual === 0 ? -6 : 1 - diaSemanaAtual
    const segunda = new Date(hoje)
    segunda.setDate(hoje.getDate() + diffSegunda + (semanaOffset * 7))
    return Array.from({ length: 5 }, (_, i) => {
      const d = new Date(segunda)
      d.setDate(segunda.getDate() + i)
      return d
    })
  }, [semanaOffset])

  const carregarDados = async () => {
    try {
      const result = await agendaService.getAll()
      setAgendamentos(result)
    } catch (e) { console.error(e) }
    try {
      const result = await professorService.getAll()
      setProfessores(result.data.filter(p => p.ativo))
    } catch (e) { console.error(e) }
    try {
      const result = await clienteService.getAll(1, 1000)
      setClientes(Array.isArray(result.data) ? result.data : [])
    } catch (e) { console.error(e) }
  }

  React.useEffect(() => { carregarDados() }, [])

  const getAgendamentosParaDia = (data: Date): IAgendamento[] => {
    const dataStr = formatarDataLocal(data)
    return agendamentos.filter(a => {
      if (!a.dataAgendamento) return false
      return a.dataAgendamento.split('T')[0] === dataStr
    })
  }

  const getAgendamentosParaHora = (data: Date, hora: number): IAgendamento[] => {
    return getAgendamentosParaDia(data).filter(a => {
      const hInicio = parseInt(a.horaInicio?.substring(11, 13) || '0', 10)
      return hInicio === hora
    })
  }

  const handleSlotClick = (data: Date, hora: number) => {
    const agendamentosNoHorario = getAgendamentosParaHora(data, hora)
    if (agendamentosNoHorario.length > 0) {
      setClientesNoHorario(agendamentosNoHorario)
      setModalHorarioInfo(`${DIAS_SEMANA_FULL[getDiasDaSemana.indexOf(data)]} — ${String(hora).padStart(2, '0')}:00`)
      setModalSlotData(data)
      setModalSlotHora(hora)
      setModalDetalheOpen(true)
    } else {
      setEditandoId(null)
      setFormProfessorId('')
      setFormClienteId('')
      setFormDiaSemana(getDiasDaSemana.indexOf(data))
      setFormHoraInicio(`${String(hora).padStart(2, '0')}:00`)
      setFormHoraFim(`${String(hora + 1).padStart(2, '0')}:00`)
      setModalFormOpen(true)
    }
  }

  const resolverSobreposicao = (agendamentos: IAgendamento[]): (IAgendamento & { colIndex: number; totalCols: number })[] => {
    if (agendamentos.length <= 1) return agendamentos.map(a => ({ ...a, colIndex: 0, totalCols: 1 }))

    const ordenados = [...agendamentos].sort((a, b) => {
      const iniA = horasParaMinutos(a.horaInicio?.substring(11, 16) || '08:00')
      const iniB = horasParaMinutos(b.horaInicio?.substring(11, 16) || '08:00')
      return iniA - iniB
    })

    const result: (IAgendamento & { colIndex: number; totalCols: number })[] = []
    const grupos: IAgendamento[][] = []

    for (const ag of ordenados) {
      const iniAg = horasParaMinutos(ag.horaInicio?.substring(11, 16) || '08:00')
      const fimAg = horasParaMinutos(ag.horaFim?.substring(11, 16) || '09:00')
      let inserido = false

      for (const grupo of grupos) {
        const sobrepoe = grupo.some(g => {
          const iniG = horasParaMinutos(g.horaInicio?.substring(11, 16) || '08:00')
          const fimG = horasParaMinutos(g.horaFim?.substring(11, 16) || '09:00')
          return iniAg < fimG && fimAg > iniG
        })
        if (sobrepoe) {
          grupo.push(ag)
          inserido = true
          break
        }
      }
      if (!inserido) grupos.push([ag])
    }

    for (const grupo of grupos) {
      const totalCols = grupo.length
      grupo.forEach((ag, idx) => {
        result.push({ ...ag, colIndex: idx, totalCols })
      })
    }

    return result
  }

  const handleEditar = (agendamento: IAgendamento) => {
    setEditandoId(agendamento.id)
    setFormProfessorId(agendamento.professorId)
    setFormClienteId(agendamento.clienteId)
    const horaInicio = agendamento.horaInicio?.substring(11, 16) || '08:00'
    const horaFim = agendamento.horaFim?.substring(11, 16) || '09:00'
    setFormHoraInicio(horaInicio)
    setFormHoraFim(horaFim)
    setModalDetalheOpen(false)
    setModalFormOpen(true)
  }

  const handleExcluir = async (id: string) => {
    if (!window.confirm('Deseja excluir este agendamento?')) return
    try {
      await agendaService.inativar(id)
      setModalDetalheOpen(false)
      carregarDados()
    } catch (e) { console.error(e) }
  }

  const handleSalvar = async () => {
    if (!formProfessorId || !formClienteId) return
    const diaSelecionado = getDiasDaSemana[formDiaSemana]
    const dataStr = formatarDataLocal(diaSelecionado)

    const obj = {
      professorId: formProfessorId,
      clienteId: formClienteId,
      diaSemana: formDiaSemana,
      dataAgendamento: `${dataStr}T00:00:00`,
      horaInicio: `${dataStr}T${formHoraInicio}:00`,
      horaFim: `${dataStr}T${formHoraFim}:00`,
      ativo: true
    }

    if (editandoId) {
      await agendaService.atualizar(editandoId, obj)
    } else {
      await agendaService.cadastrar(obj)
    }
    setModalFormOpen(false)
    setEditandoId(null)
    carregarDados()
  }

  const calcularCargaHoraria = React.useMemo(() => {
    const carga: Record<string, number> = {}
    agendamentos.forEach(a => {
      if (!carga[a.professorId]) carga[a.professorId] = 0
      const hIni = a.horaInicio?.substring(11, 16)
      const hFim = a.horaFim?.substring(11, 16)
      if (hIni && hFim) {
        carga[a.professorId] += (horasParaMinutos(hFim) - horasParaMinutos(hIni)) / 60
      }
    })
    return carga
  }, [agendamentos])

  const horaFimOptions = HORARIOS_COMPLETOS.filter(h => h > formHoraInicio)

  return (
    <TitleScreenCrud title="Agenda Psicomotricidade">
      <MDBox padding={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button onClick={() => setSemanaOffset(s => s - 1)} variant="outlined" size="small"
            sx={{ color: '#7b0b99', borderColor: '#7b0b99', '&:hover': { borderColor: '#5a0a75', color: '#5a0a75' } }}>← Anterior</Button>
          <MDTypography variant="h6">
            {getDiasDaSemana[0]?.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
            {' — '}
            {getDiasDaSemana[4]?.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </MDTypography>
          <Button onClick={() => setSemanaOffset(s => s + 1)} variant="outlined" size="small"
            sx={{ color: '#7b0b99', borderColor: '#7b0b99', '&:hover': { borderColor: '#5a0a75', color: '#5a0a75' } }}>Próxima →</Button>
        </MDBox>

        <GradeWrapper>
          <GradeHeaderRow>
            <GradeHeaderCell>Horário</GradeHeaderCell>
            {getDiasDaSemana.map((data, i) => (
              <GradeHeaderCell key={i}>
                {DIAS_SEMANA[i]}<br />
                {data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
              </GradeHeaderCell>
            ))}
          </GradeHeaderRow>

          <GradeBody>
            <GradeTimeColumn>
              {HORAS.map(h => (
                <GradeTimeSlot key={h}>{String(h).padStart(2, '0')}:00</GradeTimeSlot>
              ))}
            </GradeTimeColumn>

            <GradeDaysGrid>
              {getDiasDaSemana.map((data, idxDia) => (
                <GradeDayColumn key={idxDia}>
                  {HORAS.map(h => (
                    <GradeHourLine key={h} onClick={() => handleSlotClick(data, h)} />
                  ))}
                  {resolverSobreposicao(getAgendamentosParaDia(data)).map(a => {
                    const { top, height } = calcularPosicaoAgendamento(a)
                    const cor = getCorProfessor(a.professorId + '-' + a.id)
                    const colWidth = 100 / a.totalCols
                    const colLeft = colWidth * a.colIndex
                    return (
                      <BlocoHorario
                        key={a.id}
                        cor={cor.bg}
                        topPx={top}
                        heightPx={height}
                        style={{ borderColor: cor.border, left: `calc(${colLeft}% + 3px)`, width: `calc(${colWidth}% - 6px)` }}
                      >
                        <div style={{ paddingRight: 48 }}>
                          <strong>{a.professorNome || 'Professor'}</strong>
                          {a.clienteNome || 'Aluno'}
                          <div style={{ fontSize: 9, opacity: 0.7 }}>
                            {a.horaInicio?.substring(11, 16)}–{a.horaFim?.substring(11, 16)}
                          </div>
                        </div>
                        <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleEditar(a) }}
                            sx={{ bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', '&:hover': { bgcolor: '#e3f2fd' }, width: 24, height: 24 }}
                          >
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleExcluir(a.id) }}
                            sx={{ bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', '&:hover': { bgcolor: '#ffebee' }, width: 24, height: 24 }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </BlocoHorario>
                    )
                  })}
                </GradeDayColumn>
              ))}
            </GradeDaysGrid>
          </GradeBody>
        </GradeWrapper>
      </MDBox>

      <MDBox padding={3}>
        <MDTypography variant="h6" mb={2}>Carga Horária Semanal por Professor</MDTypography>
        {professores.map(p => {
          const horas = calcularCargaHoraria[p.id] || 0
          const total = 40
          return (
            <CargaHorariaCard key={p.id}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="button" fontWeight="medium">{p.nome}</MDTypography>
                <MDTypography variant="caption" color="text">
                  {horas.toFixed(1)}h / {total}h ({(total - horas).toFixed(1)}h disponíveis)
                </MDTypography>
              </MDBox>
              <CargaBar percentual={(horas / total) * 100} />
            </CargaHorariaCard>
          )
        })}
      </MDBox>

      <MDBox display="flex" justifyContent="flex-end" pr={3} pb={3}>
        <IconButton
          onClick={() => {
            setEditandoId(null)
            setFormProfessorId('')
            setFormClienteId('')
            setFormDiaSemana(0)
            setFormHoraInicio('08:00')
            setFormHoraFim('09:00')
            setModalFormOpen(true)
          }}
          style={{ width: 50, height: 50, backgroundColor: '#FFF', borderRadius: 50, border: '1px solid #e0e0e0' }}
        >
          <AddCircleRoundedIcon style={{ width: 40, height: 40, color: '#7b0b99' }} />
        </IconButton>
      </MDBox>

      {/* Modal Detalhe — clientes no horário */}
      <Dialog open={modalDetalheOpen} onClose={() => setModalDetalheOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Agendamentos — {modalHorarioInfo}
          <IconButton onClick={() => setModalDetalheOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', minHeight: 150 }}>
          {clientesNoHorario.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>Nenhum agendamento neste horário.</Typography>
          ) : (
            clientesNoHorario.map(a => (
              <ModalClienteItem key={a.id}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">{a.clienteNome || 'Aluno'}</Typography>
                  <Typography variant="caption" color="text.secondary">{a.professorNome || 'Professor'}</Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip label={`${a.horaInicio?.substring(11, 16)}–${a.horaFim?.substring(11, 16)}`} size="small" color="primary" variant="outlined" />
                  <IconButton size="small" color="info" onClick={() => handleEditar(a)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleExcluir(a.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ModalClienteItem>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalDetalheOpen(false)}>Fechar</Button>
          <Button
            variant="contained"
            style={{ backgroundColor: '#7b0b99', color: '#FFF' }}
            onClick={() => {
              setModalDetalheOpen(false)
              setEditandoId(null)
              setFormProfessorId('')
              setFormClienteId('')
              if (modalSlotData) {
                setFormDiaSemana(getDiasDaSemana.indexOf(modalSlotData))
              }
              setFormHoraInicio(`${String(modalSlotHora).padStart(2, '0')}:00`)
              setFormHoraFim(`${String(modalSlotHora + 1).padStart(2, '0')}:00`)
              setModalFormOpen(true)
            }}
          >
            + Novo Agendamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Form — criar/editar */}
      <Dialog open={modalFormOpen} onClose={() => setModalFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {editandoId ? 'Editar Agendamento' : 'Novo Agendamento'}
          <IconButton onClick={() => setModalFormOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', minHeight: 400 }}>
          <MDBox display="flex" flexDirection="column" gap={4} py={2}>
            <TextField select label="Professor" value={formProfessorId} onChange={e => setFormProfessorId(e.target.value)} fullWidth size="medium"
              sx={{ '& .MuiInputBase-root': { height: 56 } }}>
              {professores.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Aluno (Cliente)" value={formClienteId} onChange={e => setFormClienteId(e.target.value)} fullWidth size="medium"
              sx={{ '& .MuiInputBase-root': { height: 56 } }}>
              {clientes.map((c: any) => (
                <MenuItem key={c.id} value={c.id}>{c.nomeCliente || c.nome}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Dia da Semana" value={formDiaSemana} onChange={e => setFormDiaSemana(Number(e.target.value))} fullWidth size="medium"
              sx={{ '& .MuiInputBase-root': { height: 56 } }}>
              {getDiasDaSemana.map((data, i) => (
                <MenuItem key={i} value={i}>{DIAS_SEMANA_FULL[i]} — {data.toLocaleDateString('pt-BR')}</MenuItem>
              ))}
            </TextField>
            <MDBox display="flex" gap={2}>
              <TextField select label="Horário Início" value={formHoraInicio} onChange={e => { setFormHoraInicio(e.target.value); setFormHoraFim('') }} fullWidth size="medium"
                sx={{ '& .MuiInputBase-root': { height: 56 } }}>
                {HORARIOS_COMPLETOS.map(h => (
                  <MenuItem key={h} value={h}>{h}</MenuItem>
                ))}
              </TextField>
              <TextField select label="Horário Fim" value={formHoraFim} onChange={e => setFormHoraFim(e.target.value)} fullWidth size="medium" disabled={!formHoraInicio}
                sx={{ '& .MuiInputBase-root': { height: 56 } }}>
                {horaFimOptions.map(h => (
                  <MenuItem key={h} value={h}>{h}</MenuItem>
                ))}
              </TextField>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalFormOpen(false)}>Cancelar</Button>
          <Button onClick={handleSalvar} variant="contained" style={{ backgroundColor: '#7b0b99', color: '#FFF' }}
            disabled={!formProfessorId || !formClienteId || !formHoraFim}>
            {editandoId ? 'Atualizar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </TitleScreenCrud>
  )
}
